import React from 'react';
import Icon from 'components/AppIcon';

const GOALS = [
  { id: 'lose_weight', label: 'Lose Weight', icon: 'TrendingDown', color: 'var(--color-destructive)' },
  { id: 'build_muscle', label: 'Build Muscle', icon: 'Dumbbell', color: 'var(--color-primary)' },
  { id: 'improve_endurance', label: 'Improve Endurance', icon: 'Wind', color: '#0ea5e9' },
  { id: 'increase_flexibility', label: 'Increase Flexibility', icon: 'Flower2', color: 'var(--color-success)' },
  { id: 'general_fitness', label: 'General Fitness', icon: 'Heart', color: 'var(--color-warning)' },
  { id: 'sports_performance', label: 'Sports Performance', icon: 'Trophy', color: 'var(--color-accent)' },
];

const TIMELINES = [
  { id: '1_month', label: '1 Month', desc: 'Quick start' },
  { id: '3_months', label: '3 Months', desc: 'Short term' },
  { id: '6_months', label: '6 Months', desc: 'Mid term' },
  { id: '1_year', label: '1 Year', desc: 'Long term' },
];

export default function GoalStep({ data, onChange, errors }) {
  const handleChange = (field, value) => onChange({ ...data, [field]: value });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Set Your Goals</h2>
        <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">Define what success looks like for your fitness journey.</p>
      </div>
      {/* Primary Goal */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-3">
          Primary Fitness Goal <span className="text-[var(--color-destructive)]">*</span>
        </label>
        {errors?.primaryGoal && <p className="text-xs text-[var(--color-destructive)] mb-2">{errors?.primaryGoal}</p>}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {GOALS?.map((goal) => {
            const isSelected = data?.primaryGoal === goal?.id;
            return (
              <button
                key={goal?.id}
                type="button"
                onClick={() => handleChange('primaryGoal', goal?.id)}
                className={`flex flex-col items-center gap-2.5 p-4 rounded-[var(--radius-md)] border-2 transition-all duration-200 ${
                  isSelected
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/8 shadow-sm'
                    : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/40 bg-[var(--color-card)]'
                }`}
              >
                <div
                  className="w-11 h-11 rounded-[var(--radius-sm)] flex items-center justify-center"
                  style={{ backgroundColor: isSelected ? `${goal?.color}20` : 'var(--color-muted)' }}
                >
                  <Icon name={goal?.icon} size={20} color={isSelected ? goal?.color : 'var(--color-muted-foreground)'} strokeWidth={2} />
                </div>
                <span className={`text-xs font-semibold font-[var(--font-caption)] text-center leading-tight ${
                  isSelected ? 'text-[var(--color-primary)]' : 'text-[var(--color-foreground)]'
                }`}>{goal?.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Target Weight & Timeline */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-1.5">
            Target Weight <span className="text-[var(--color-muted-foreground)] font-normal text-xs">(optional, kg)</span>
          </label>
          <input
            type="number"
            value={data?.targetWeight || ''}
            onChange={(e) => handleChange('targetWeight', e?.target?.value)}
            placeholder="e.g. 75"
            min="30"
            max="300"
            className="w-full px-3 py-2.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm font-[var(--font-caption)] bg-[var(--color-background)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)] transition-colors"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-1.5">Goal Timeline</label>
          <div className="grid grid-cols-2 gap-2">
            {TIMELINES?.map((t) => {
              const isSelected = data?.timeline === t?.id;
              return (
                <button
                  key={t?.id}
                  type="button"
                  onClick={() => handleChange('timeline', t?.id)}
                  className={`flex flex-col items-center py-2.5 px-2 rounded-[var(--radius-sm)] border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/8'
                      : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/40 bg-[var(--color-card)]'
                  }`}
                >
                  <span className={`text-sm font-bold font-[var(--font-heading)] ${
                    isSelected ? 'text-[var(--color-primary)]' : 'text-[var(--color-foreground)]'
                  }`}>{t?.label}</span>
                  <span className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{t?.desc}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {/* Motivation Level */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-3">
          Motivation Level: <span className="text-[var(--color-primary)]">{data?.motivationLevel || 3}/5</span>
        </label>
        <div className="flex items-center gap-3">
          <Icon name="Battery" size={16} color="var(--color-muted-foreground)" />
          <input
            type="range"
            min="1"
            max="5"
            value={data?.motivationLevel || 3}
            onChange={(e) => handleChange('motivationLevel', parseInt(e?.target?.value))}
            className="flex-1 h-2 rounded-full accent-[var(--color-primary)] cursor-pointer"
          />
          <Icon name="Zap" size={16} color="var(--color-primary)" />
        </div>
        <div className="flex justify-between text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
          <span>Low</span>
          <span>Moderate</span>
          <span>High</span>
        </div>
      </div>
      {/* Notes */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-1.5">Additional Notes <span className="text-[var(--color-muted-foreground)] font-normal text-xs">(optional)</span></label>
        <textarea
          value={data?.notes || ''}
          onChange={(e) => handleChange('notes', e?.target?.value)}
          placeholder="Any injuries, health conditions, or specific goals you want us to know about..."
          rows={3}
          className="w-full px-3 py-2.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm font-[var(--font-caption)] bg-[var(--color-background)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)] transition-colors resize-none"
        />
      </div>
    </div>
  );
}
